import { request } from "../../../services/api"
import type { BaseApiResponse, BasePagination } from "../../../types/api.ts"
import { createFormDataNested } from "../../../utils/form-data/create-form-data-nested"
import type { UpdateLeadParams } from "../../Lead/redux/lead.types.ts"
import type {
  LeadsFilterOptions,
  LeadsFilters
} from "../redux/leads.types.ts"
import type { LeadListItem } from "../types/types.ts"
import type {
  ArchiveLeadError,
  ArchiveLeadParams,
  BulkLeadActionParams,
  BulkLeadResult,
  ChangeStatusLeadError,
  ChangeStatusLeadParams,
  ImportLeadsData,
  InactiveCounts,
  LeadImportPreview,
  LeadMatch,
  LeadMatchesParams,
  LeadEmailCampaignRequest,
  LeadEmailCampaignResult,
  LeadEmailTemplate,
  LeadsStats,
  TransitionLeadReasonError,
  TransitionLeadReasonParams
} from "./services.types.ts"

const BASE_URL = "/crm/leads"
const ATTACHMENTS_BASE_URL = "/crm/lead-attachments"
const PROPOSALS_BASE_URL = "/crm/proposal-approvals"

const PATHS = {
  // Leads
  getLeads: `${BASE_URL}/`,
  getFilterOptions: `${BASE_URL}/filter_options/`,
  exportLeads: `${BASE_URL}/export/`,
  getLeadDetails: (id: string) => `${BASE_URL}/${id}/details/`,
  createLead: `${BASE_URL}/`,
  updateLead: (id: string) => `${BASE_URL}/${id}/details/`,
  deleteLead: (id: string) => `${BASE_URL}/${id}/`,
  changeStatusLead: (id: string) => `${BASE_URL}/${id}/transition/`,
  archiveLead: (id: string) => `${BASE_URL}/${id}/archive/`,
  reactivateLead: (id: string) => `${BASE_URL}/${id}/reactivate/`,
  transitionLeadReason: (id: string) => `${BASE_URL}/${id}/transition-reason/`,
  inactiveCounts: `${BASE_URL}/inactive-counts/`,
  stats: `${BASE_URL}/stats/`,
  assignSalesManager: (id: string) => `${BASE_URL}/${id}/assign_sales_manager/`,
  bulkAssignOwner: `${BASE_URL}/bulk-assign-owner/`,
  bulkTransition: `${BASE_URL}/bulk-transition/`,
  bulkOperationalStates: `${BASE_URL}/bulk-operational-states/`,
  bulkFollowUp: `${BASE_URL}/bulk-follow-up/`,
  bulkArchive: `${BASE_URL}/bulk-archive/`,
  bulkDelete: `${BASE_URL}/bulk-delete/`,
  bulkEmail: `${BASE_URL}/bulk-email/`,

  emailTemplates: `/crm/lead-email-templates/`,
  emailPlaceholders: `/crm/lead-email-templates/placeholders/`,

  leadMatches: `${BASE_URL}/lead-matches/`,

  // Import
  importLeads: `${BASE_URL}/import-data/`,
  importPreview: `${BASE_URL}/import-preview/`,
  importTemplate: `${BASE_URL}/import-template/`,

  // Proposals
  getPendingProposals: `${PROPOSALS_BASE_URL}/`,
  approveProposals: (id: string) => `${PROPOSALS_BASE_URL}/${id}/approve/`,
  rejectProposals: (id: string) => `${PROPOSALS_BASE_URL}/${id}/reject/`,

  // Documents
  getDocuments: `${ATTACHMENTS_BASE_URL}/`,
  getDocumentsFilterOptions: `${ATTACHMENTS_BASE_URL}/filter_options/`,
  addDocuments: `${ATTACHMENTS_BASE_URL}/`,
  getDocumentDetails: (id: string) => `${ATTACHMENTS_BASE_URL}/${id}/`,
  getDocumentHistory: (id: string) => `${ATTACHMENTS_BASE_URL}/${id}/history/`,
  deleteDocument: (id: string) => `${ATTACHMENTS_BASE_URL}/${id}/archive/`
}

export const importLeads = (file: File) => {
  const formData = createFormDataNested({ file }, "file")

  return request.api.post<ImportLeadsData, Blob>(PATHS.importLeads, formData, {
    responseType: "blob",
    headers: { "Content-Type": "multipart/form-data" }
  })
}

export const getImportLeadsTemplates = () => {
  return request.api.get<Blob, BaseApiResponse<unknown>>(
    PATHS.importTemplate,
    {},
    { responseType: "blob" }
  )
}

export const exportLeads = (
  params?: Partial<LeadsFilters> & { ids?: string }
) =>
  request.api.get<Blob, BaseApiResponse<unknown>>(PATHS.exportLeads, params, {
    responseType: "blob"
  })

export const getLeadsFilterOptions = () => {
  return request.api.get<LeadsFilterOptions, BaseApiResponse<unknown>>(
    PATHS.getFilterOptions
  )
}

export const getLeads = (params?: Partial<LeadsFilters>) =>
  request.api.get<BasePagination<LeadListItem>, BaseApiResponse<unknown>>(
    PATHS.getLeads,
    params
  )

export const updateLead = ({ id, ...newInfo }: UpdateLeadParams) => {
  const url = PATHS.updateLead(id)

  if (!newInfo?.file_list?.length) {
    return request.api.patch<LeadListItem, BaseApiResponse<unknown>>(
      url,
      newInfo
    )
  }

  const headers = { "Content-Type": "multipart/form-data" }
  const formData = createFormDataNested(newInfo, "file")

  return request.api.patch<LeadListItem, BaseApiResponse<unknown>>(
    url,
    formData,
    { headers }
  )
}

export const changeStatusLead = ({ id, ...data }: ChangeStatusLeadParams) => {
  return request.api.post<BaseApiResponse<unknown>, ChangeStatusLeadError>(
    PATHS.changeStatusLead(id),
    data
  )
}

export const archiveLead = ({ id, ...data }: ArchiveLeadParams) =>
  request.api.post<BaseApiResponse<unknown>, ArchiveLeadError>(
    PATHS.archiveLead(id),
    data
  )

export const reactivateLead = (id: string) =>
  request.api.post<BaseApiResponse<unknown>, BaseApiResponse<unknown>>(
    PATHS.reactivateLead(id)
  )

export const transitionLeadReason = ({
  id,
  ...data
}: TransitionLeadReasonParams) =>
  request.api.post<BaseApiResponse<unknown>, TransitionLeadReasonError>(
    PATHS.transitionLeadReason(id),
    data
  )

export const getLeadsStats = (params?: Partial<LeadsFilters>) =>
  request.api.get<LeadsStats, BaseApiResponse<unknown>>(PATHS.stats, params)

export const getInactiveCounts = (params?: Partial<LeadsFilters>) =>
  request.api.get<InactiveCounts, BaseApiResponse<unknown>>(
    PATHS.inactiveCounts,
    params
  )

export const deleteLead = (leadID: string) =>
  request.api.delete<BaseApiResponse<null>, BaseApiResponse<unknown>>(
    PATHS.deleteLead(leadID)
  )

const postBulk = (path: string, body: Record<string, unknown>) =>
  request.api.post<BulkLeadResult, BaseApiResponse<unknown>>(path, body)

export const bulkUpdateLeads = ({
  action,
  ids,
  ...fields
}: BulkLeadActionParams) => {
  switch (action) {
    case "assign-owner":
      return postBulk(PATHS.bulkAssignOwner, { ids, manager: fields.manager })
    case "change-stage":
      return postBulk(PATHS.bulkTransition, {
        ids,
        target_status: fields.target_status
      })
    case "operational-states":
      return postBulk(PATHS.bulkOperationalStates, {
        ids,
        operational_states: fields.operational_states,
        // Follow-Up is refused without these, so they are collected alongside
        // the states rather than in a second pass.
        ...(fields.follow_up_owner
          ? { follow_up_owner: fields.follow_up_owner }
          : {}),
        ...(fields.follow_up_date
          ? { follow_up_date: fields.follow_up_date }
          : {}),
        ...(fields.follow_up_time
          ? { follow_up_time: fields.follow_up_time }
          : {})
      })
    case "follow-up":
      return postBulk(PATHS.bulkFollowUp, {
        ids,
        follow_up_owner: fields.follow_up_owner,
        follow_up_date: fields.follow_up_date,
        follow_up_time: fields.follow_up_time
      })
    case "close-lost":
      return postBulk(PATHS.bulkArchive, {
        ids,
        target_status: fields.target_status,
        lost_reason: fields.lost_reason,
        ...(fields.lost_notes?.trim()
          ? { lost_notes: fields.lost_notes.trim() }
          : {}),
        // Set on the retry after the user has been told which leads still
        // have meetings booked; without it the server refuses each of those
        // leads and the selection can never go through.
        ...(fields.cancel_appointments ? { cancel_appointments: true } : {})
      })
    case "delete":
      return postBulk(PATHS.bulkDelete, { ids })
  }
}

export const sendLeadEmailCampaign = (body: LeadEmailCampaignRequest) =>
  request.api.post<LeadEmailCampaignResult, BaseApiResponse<unknown>>(
    PATHS.bulkEmail,
    body
  )

export const getLeadEmailTemplates = () =>
  request.api.get<BasePagination<LeadEmailTemplate>, BaseApiResponse<unknown>>(
    PATHS.emailTemplates
  )

export const getLeadEmailPlaceholders = () =>
  request.api.get<{ placeholders: string[] }, BaseApiResponse<unknown>>(
    PATHS.emailPlaceholders
  )

export const getLeadMatches = (params: LeadMatchesParams) =>
  request.api.get<LeadMatch[], BaseApiResponse<void>>(PATHS.leadMatches, params)

export const previewLeadImport = (file: File) =>
  request.api.post<LeadImportPreview, BaseApiResponse<void>>(
    PATHS.importPreview,
    createFormDataNested({ file }, "file"),
    { headers: { "Content-Type": "multipart/form-data" } }
  )
